import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Backdrop,
  Divider,
  Button,
  MenuItem,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import DragIndicator from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import materialService, { Material } from '../../services/materialService';
import { useNotification } from '../layouts/Layout';

interface GoodsAndServicesTabProps {
  selectedProject?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`goods-services-tabpanel-${index}`}
      aria-labelledby={`goods-services-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const GoodsAndServicesTab: React.FC<GoodsAndServicesTabProps> = ({ selectedProject }) => {
  const [tabValue, setTabValue] = useState(0); // 0 = Towary, 1 = Usługi
  const [materials, setMaterials] = useState<Material[]>([]);
  const [services, setServices] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tableHeight, setTableHeight] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { showNotification } = useNotification();

  // Kolumny tabeli
  const ALL_COLUMNS = ['data', 'symbol', 'kontrahent', 'miejscowosc', 'ilosc', 'jm', 'cena_jedn', 'email', 'akcja'];
  const COLUMNS_LABELS: Record<string, string> = {
    data: 'Data',
    symbol: 'Symbol',
    kontrahent: 'Kontrahent',
    miejscowosc: 'Miejscowość',
    ilosc: 'Ilość',
    jm: 'J.m.',
    cena_jedn: 'Cena jedn.',
    email: 'Email',
    akcja: 'Akcja',
  };

  // Stan dla dostosowywania kolumn
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(VISIBLE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const result: Record<string, boolean> = {};
        ALL_COLUMNS.forEach(col => {
          result[col] = parsed[col] !== false;
        });
        return result;
      }
    } catch {}
    const defaultVisible: Record<string, boolean> = {};
    ALL_COLUMNS.forEach(col => {
      defaultVisible[col] = true;
    });
    return defaultVisible;
  });
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(ORDER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const result = parsed.filter((col: string) => ALL_COLUMNS.includes(col));
        const missing = ALL_COLUMNS.filter(col => !result.includes(col));
        return [...result, ...missing];
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsOpen, setColsOpen] = useState(false);
  const [colsPosition, setColsPosition] = useState<{ top?: number; left?: number }>({ top: undefined, left: undefined });
  const [colsDragging, setColsDragging] = useState<{ startX: number; startY: number; top?: number; left?: number } | null>(null);
  const [colResizeMode, setColResizeMode] = useState(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number } | null>(null);
  
  // Szerokości kolumn
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    data: 100,
    symbol: 180,
    kontrahent: 180,
    miejscowosc: 180,
    ilosc: 80,
    jm: 80,
    cena_jedn: 100,
    email: 180,
    akcja: 60,
  }), []);
  
  const STORAGE_KEY = 'goodsServicesTableColWidths';
  const WIDTHS_KEY = `${STORAGE_KEY}:widths`;
  const VISIBLE_KEY = `${STORAGE_KEY}:visible`;
  const ORDER_KEY = `${STORAGE_KEY}:order`;
  
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem(WIDTHS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          const result: Record<string, number> = {};
          ALL_COLUMNS.forEach(col => {
            result[col] = parsed[col] ?? defaultColumnWidths[col] ?? 100;
          });
          return result;
        }
      }
    } catch {}
    return { ...defaultColumnWidths };
  });
  
  const getColWidthPx = React.useCallback((key: string): string => {
    const def = defaultColumnWidths[key] ?? 100;
    const cur = columnWidths[key];
    const width = typeof cur === 'number' ? Math.max(cur, def) : def;
    return `${width}px`;
  }, [columnWidths, defaultColumnWidths]);
  
  const colV = React.useCallback((key: string) => (draftColumnsVisible ?? columnsVisible)[key] !== false, [columnsVisible, draftColumnsVisible]);

  const LIMIT = 100;

  // Pobieranie danych
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setCurrentPage(1);
      setHasMore(false);
      
      if (tabValue === 0) {
        // Towary - pobierz pierwsze 100 materiałów
        const response = await materialService.getAllMaterials(1, LIMIT);
        console.log('Odpowiedź API:', response);
        if (response && response.materials && response.pagination) {
          setMaterials(response.materials);
          setTotalCount(response.pagination.total);
          setHasMore(response.materials.length === LIMIT && response.materials.length < response.pagination.total);
        } else {
          console.error('Nieprawidłowy format odpowiedzi:', response);
          setMaterials([]);
          setTotalCount(0);
          setHasMore(false);
        }
      } else {
        // Usługi - na razie pusta lista
        setServices([]);
        setTotalCount(0);
        setHasMore(false);
      }
      setCurrentPage(1);
    } catch (error: any) {
      console.error('Błąd podczas pobierania danych:', error);
      console.error('Szczegóły błędu:', error.response?.data || error.message);
      setMaterials([]);
      setServices([]);
      setTotalCount(0);
      showNotification(`Błąd podczas pobierania danych: ${error.response?.data?.message || error.message || 'Nieznany błąd'}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [tabValue, showNotification]);

  // Ładowanie kolejnych danych (infinite scroll)
  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      
      if (tabValue === 0) {
        const response = await materialService.getAllMaterials(nextPage, LIMIT);
        setMaterials(prev => [...prev, ...response.materials]);
        setHasMore(response.materials.length === LIMIT && materials.length + response.materials.length < response.pagination.total);
        setCurrentPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Błąd podczas ładowania kolejnych danych:', error);
      showNotification('Błąd podczas ładowania kolejnych danych', 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore, tabValue, materials.length, showNotification]);

  // Obsługa aktualizacji danych
  const handleRefresh = () => {
    setMaterials([]);
    setServices([]);
    setCurrentPage(1);
    setHasMore(false);
    loadInitialData();
  };

  // Automatyczne ładowanie danych przy pierwszym renderowaniu i zmianie zakładki
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Obsługa przeciągania okna "Dostosuj kolumny"
  useEffect(() => {
    if (!colsDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - colsDragging.startX;
      const deltaY = e.clientY - colsDragging.startY;
      setColsPosition({
        top: Math.max(0, Math.min(window.innerHeight - 400, (colsDragging.top || window.innerHeight) + deltaY)),
        left: Math.max(0, Math.min(window.innerWidth - 280, (colsDragging.left || window.innerWidth) + deltaX)),
      });
    };
    const handleMouseUp = () => {
      setColsDragging(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [colsDragging]);

  // ESC zamyka okno "Dostosuj kolumny"
  useEffect(() => {
    if (!colsOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDragCol(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [colsOpen]);

  // Zapisywanie szerokości kolumn
  useEffect(() => {
    try {
      const payload = JSON.stringify(columnWidths);
      localStorage.setItem(WIDTHS_KEY, payload);
    } catch {}
  }, [columnWidths, WIDTHS_KEY]);

  // Zapisywanie widoczności kolumn
  useEffect(() => {
    try {
      const payload = JSON.stringify(columnsVisible);
      localStorage.setItem(VISIBLE_KEY, payload);
    } catch {}
  }, [columnsVisible, VISIBLE_KEY]);

  // Zapisywanie kolejności kolumn
  useEffect(() => {
    try {
      const payload = JSON.stringify(columnOrder);
      localStorage.setItem(ORDER_KEY, payload);
    } catch {}
  }, [columnOrder, ORDER_KEY]);

  // Obsługa zmiany szerokości kolumn
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => { 
      e.preventDefault(); 
      setDragCol(null); 
    };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol]);

  // Funkcje renderujące
  const renderResizableTh = React.useCallback((keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell
      align={align}
      sx={{
        width: getColWidthPx(keyId),
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: '#FAFAFA',
        borderRight: keyId === (draftOrder ?? columnOrder)[(draftOrder ?? columnOrder).filter(c => colV(c)).length - 1] ? 'none' : '1px solid #d4d4d9',
        borderRadius: 0,
        boxShadow: 'none',
        userSelect: 'none',
        padding: '1px 4px',
        fontSize: '0.85rem'
      }}
    >
      {node}
      {(colResizeMode) && (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            const currentWidth = parseInt(getColWidthPx(keyId)) || (columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 100);
            setDragCol({ key: keyId, startX: e.clientX, startW: currentWidth }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '12px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 6,
            touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
          }}
        />
      )}
    </TableCell>
  ), [getColWidthPx, colResizeMode, draftOrder, columnOrder, colV, columnWidths, defaultColumnWidths]);

  const renderColGroup = React.useCallback((order: string[]) => (
    <colgroup>
      {order.map((key) => (
        colV(key) ? <col key={`col-${key}`} style={{ width: getColWidthPx(key) }} /> : null
      ))}
    </colgroup>
  ), [colV, getColWidthPx]);

  // Obsługa infinite scroll
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore) {
        loadMoreData();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loadingMore, loadMoreData]);

  // Obliczanie wysokości tabeli
  useEffect(() => {
    const measureHeights = () => {
      try {
        const root = tableContainerRef.current as HTMLElement | null;
        if (!root) return;
        const top = root.getBoundingClientRect().top;
        const viewportAvail = Math.max(300, Math.floor((window.innerHeight || 0) - top - 80));
        setTableHeight(viewportAvail);
      } catch {}
    };
    const t = setTimeout(measureHeights, 0);
    const t2 = setTimeout(measureHeights, 100);
    window.addEventListener('resize', measureHeights);
    return () => { 
      clearTimeout(t); 
      clearTimeout(t2); 
      window.removeEventListener('resize', measureHeights); 
    };
  }, [materials.length, services.length]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const currentData = tabValue === 0 ? materials : services;
  const currentDataCount = tabValue === 0 ? materials.length : services.length;

  // Stan dla windowingu - renderuj tylko część wierszy
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const ROW_HEIGHT = 36; // Przybliżona wysokość wiersza
  const VISIBLE_BUFFER = 50; // Liczba wierszy do renderowania jednocześnie

  // Memoizacja danych - renderuj tylko widoczne wiersze + buffer
  const visibleRows = React.useMemo(() => {
    const endIndex = Math.min(visibleStartIndex + VISIBLE_BUFFER, currentData.length);
    return currentData.slice(visibleStartIndex, endIndex);
  }, [currentData, visibleStartIndex]);

  // Aktualizacja widocznych wierszy przy scrollu
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || currentData.length === 0) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const newStartIndex = Math.floor(scrollTop / ROW_HEIGHT);
      const clampedStart = Math.max(0, Math.min(newStartIndex - 10, currentData.length - VISIBLE_BUFFER));
      
      if (Math.abs(clampedStart - visibleStartIndex) > 5) {
        setVisibleStartIndex(clampedStart);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentData.length, visibleStartIndex]);

  // Komponent wiersza z memoizacją
  const TableRowMemo = React.memo(({ item, colOrder, colVisible }: { item: Material, colOrder: string[], colVisible: Record<string, boolean> }) => {
    const renderCell = (colKey: string) => {
      switch (colKey) {
        case 'data':
          return (
            <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.date ? new Date(item.date).toISOString().split('T')[0] : ''}
            </TableCell>
          );
        case 'symbol':
          return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.symbol}</TableCell>;
        case 'kontrahent':
          return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.client || ''}</TableCell>;
        case 'miejscowosc':
          return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.location}</TableCell>;
        case 'ilosc':
          return (
            <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} align="right">
              {item.quantity ? parseFloat(item.quantity.toString()).toLocaleString('pl-PL') : '0'}
            </TableCell>
          );
        case 'jm':
          return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} align="center">{item.unit || ''}</TableCell>;
        case 'cena_jedn':
          return (
            <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} align="right">
              {item.unit_price ? parseFloat(item.unit_price.toString()).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </TableCell>
          );
        case 'email':
          return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.email || ''}</TableCell>;
        case 'akcja':
          return (
            <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Tutaj można dodać menu kontekstowe w przyszłości
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </TableCell>
          );
        default:
          return null;
      }
    };

    return (
      <TableRow
        hover
        key={item.id || item.material_id}
        sx={{ '&:hover': { backgroundColor: '#F5F5FF' } }}
      >
        {colOrder.map((colKey) => {
          if (colVisible[colKey] === false) return null;
          return renderCell(colKey);
        })}
      </TableRow>
    );
  }, (prevProps, nextProps) => {
    return prevProps.item.id === nextProps.item.id && 
           prevProps.item.material_id === nextProps.item.material_id;
  });

  return (
    <Box>
      {/* Zakładki Towary/Usługi */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '12px', mb: 0, flexGrow: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="goods and services tabs"
            sx={{ 
              minHeight: 40,
              height: 40,
              flexGrow: 1,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTabs-flexContainer': { gap: '2px' }
            }}
          >
            <Tab 
              disableRipple
              label={`Towary (${totalCount > 0 ? totalCount : materials.length})`}
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
            <Tab 
              disableRipple
              label={`Usługi (${services.length})`}
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
          </Tabs>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mt: '26.5px' }}>
          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              bgcolor: 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
          >
            <Box
              component="img"
              alt="aktualizuj"
              src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
              sx={{ width: 24, height: 24 }}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              setDraftColumnsVisible({ ...columnsVisible });
              setDraftOrder([...columnOrder]);
              // Ustaw pozycję w prawym dolnym rogu przy każdym otwieraniu
              const dialogHeight = Math.min(400, (window?.innerHeight || 800) * 0.5); // 50vh lub maksymalnie 400px
              const dialogWidth = 280;
              setColsPosition({ 
                top: Math.max(20, (window?.innerHeight || 800) - dialogHeight - 20),
                left: Math.max(20, (window?.innerWidth || 1200) - dialogWidth - 20)
              });
              setColsOpen(true);
              setColResizeMode(true);
            }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
          >
            <Box
              component="img"
              alt="kolumny"
              src="/assets/icons/frame-344.svg"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Tabela */}
      <TableContainer 
        ref={tableContainerRef}
        component={Paper} 
        sx={{ 
          height: tableHeight ?? 432, 
          overflow: 'auto', 
          position: 'relative', 
          border: '1px solid #d0d0d6', 
          borderBottom: '1px solid #d0d0d6', 
          borderRadius: 0, 
          borderTopRightRadius: 4, 
          borderBottomLeftRadius: 4, 
          borderBottomRightRadius: 4, 
          overflowX: 'hidden', 
          overflowY: 'auto', 
          boxShadow: 'none', 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none', 
          '&::-webkit-scrollbar': { width: 0, height: 0 } 
        }}
      >
        <Table 
          size="small" 
          stickyHeader 
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { 
              padding: '1px 4px', 
              fontSize: '0.85rem', 
              borderRadius: 0 
            },
            '& .MuiTableCell-head': { 
              position: 'sticky', 
              top: 0, 
              zIndex: 2, 
              backgroundColor: '#FAFAFA', 
              borderRight: '1px solid #d4d4d9', 
              borderRadius: 0, 
              boxShadow: 'none' 
            },
            '& .MuiTableCell-body': { 
              borderRight: 'none', 
              borderRadius: 0 
            },
            '& .MuiTableRow-head > *:last-child': { 
              borderRight: 'none', 
              borderTopRightRadius: 4 
            }
          }}
        >
          {renderColGroup(draftOrder ?? columnOrder)}
          <TableHead>
            <TableRow>
              {(draftOrder ?? columnOrder).map((colKey) => {
                if (!colV(colKey)) return null;
                const aligns: Record<string, 'right' | 'center' | undefined> = {
                  ilosc: 'right',
                  jm: 'center',
                  cena_jedn: 'right',
                };
                return renderResizableTh(colKey, COLUMNS_LABELS[colKey] || colKey, aligns[colKey]);
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center">Ładowanie danych...</TableCell>
              </TableRow>
            ) : currentData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center">Brak danych do wyświetlenia</TableCell>
              </TableRow>
            ) : (
              <>
                {/* Spacer przed widocznymi wierszami */}
                {visibleStartIndex > 0 && (
                  <TableRow>
                    <TableCell 
                      colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} 
                      sx={{ height: visibleStartIndex * ROW_HEIGHT, padding: 0, border: 'none' }}
                    />
                  </TableRow>
                )}
                {/* Widoczne wiersze */}
                {visibleRows.map((item, idx) => (
                  <TableRowMemo
                    key={item.id || item.material_id || `row-${visibleStartIndex + idx}`}
                    item={item}
                    colOrder={draftOrder ?? columnOrder}
                    colVisible={draftColumnsVisible ?? columnsVisible}
                  />
                ))}
                {/* Spacer po widocznych wierszach */}
                {visibleStartIndex + visibleRows.length < currentData.length && (
                  <TableRow>
                    <TableCell 
                      colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} 
                      sx={{ height: (currentData.length - visibleStartIndex - visibleRows.length) * ROW_HEIGHT, padding: 0, border: 'none' }}
                    />
                  </TableRow>
                )}
              </>
            )}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Informacja o liczbie załadowanych rekordów */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" component="span">
          Wyświetlono: {currentDataCount} z {totalCount > 0 ? totalCount : currentDataCount} {tabValue === 0 ? 'towarów' : 'usług'}
        </Typography>
      </Box>

      {/* Okno „Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              height: '50vh',
              width: '280px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
              borderRadius: 0,
              zIndex: (theme) => theme.zIndex.modal + 2,
              backgroundColor: '#fff',
              top: colsPosition.top !== undefined ? `${colsPosition.top}px` : 'auto',
              left: colsPosition.left !== undefined ? `${colsPosition.left}px` : 'auto',
              bottom: colsPosition.top === undefined ? 0 : 'auto',
              right: colsPosition.left === undefined ? 0 : 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e)=> e.stopPropagation()}
          >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); setDraftOrder(null); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ 
                    e.preventDefault(); 
                    const src = e.dataTransfer.getData('text/plain'); 
                    if (!src || src===key) return; 
                    setDraftOrder(prev=>{ 
                      const cur = [...(prev||ord)]; 
                      const from = cur.indexOf(src); 
                      const to = cur.indexOf(key); 
                      if (from<0||to<0) return cur; 
                      cur.splice(to,0,cur.splice(from,1)[0]); 
                      return cur; 
                    }); 
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{COLUMNS_LABELS[key] || key.toUpperCase()}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              )); 
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); setDraftOrder(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button 
              variant="contained" 
              sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} 
              onClick={()=>{ 
                if (draftColumnsVisible) { 
                  setColumnsVisible(draftColumnsVisible); 
                  try { 
                    const payload = JSON.stringify(draftColumnsVisible); 
                    localStorage.setItem(VISIBLE_KEY, payload); 
                  } catch {} 
                } 
                if (draftOrder) { 
                  const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); 
                  setColumnOrder(normalized); 
                  try { 
                    const payload = JSON.stringify(normalized); 
                    localStorage.setItem(ORDER_KEY, payload); 
                  } catch {} 
                } 
                setColsOpen(false); 
                setColResizeMode(false); 
                setDraftColumnsVisible(null);
                setDraftOrder(null);
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default GoodsAndServicesTab;

